import { Button } from "./ui/button";
import { ArrowRight, BookOpen, Star, Zap, TrendingUp, Calendar, Hash, Heart } from "lucide-react";
import { useState, useEffect } from "react";

interface HeroProps {
  onSignUp: () => void;
  onScrollToSection: (sectionId: string) => void;
  onOpenDemo: () => void;
}

const decisionCards = [
  {
    header: "Health decisions",
    decision: "trying to go vegan for a month",
    tags: ["#diet", "#health"],
    emotions: "curious, hungry",
    rating: "7/10",
    review: "Review in 7, 15 and 30 days"
  },
  {
    header: "Career decisions",
    decision: "switching to remote work",
    tags: ["#career", "#lifestyle"],
    emotions: "excited, nervous",
    rating: "8/10",
    review: "Review in 10, 30 and 90 days"
  },
  {
    header: "Learning decisions",
    decision: "learning Spanish this year",
    tags: ["#education", "#personal"],
    emotions: "motivated, overwhelmed",
    rating: "6/10",
    review: "Review in 30, 60 and 90 days"
  },
  {
    header: "Financial decisions",
    decision: "investing in cryptocurrency",
    tags: ["#finance", "#risk"],
    emotions: "optimistic, anxious",
    rating: "5/10",
    review: "Review in 30, 90 and 180 days"
  },
  {
    header: "Life decisions",
    decision: "moving to a Chicago!",
    tags: ["#lifestyle", "#adventure"],
    emotions: "thrilled, scared",
    rating: "9/10",
    review: "Review in 60, 100 and 365 days"
  },
  {
    header: "Wellness decisions",
    decision: "starting a daily meditation practice",
    tags: ["#personal', '#wellness", "#mindfulness"],
    emotions: "hopeful, skeptical",
    rating: "7/10",
    review: "Review in 10, 21 and 90 days"
  },
  {
    header: "Relationship decisions",
    decision: "ending a toxic friendship",
    tags: ["#personal"],
    emotions: "mixed, guilty, relieved",
    rating: "5/10",
    review: "Review in 7, 30 and 90 days"
  },
  {
    header: "Major decisions",
    decision: "buying a house instead of renting",
    tags: ["#finance", "#milestone", "#home", "#investment"],
    emotions: "proud, stressed",
    rating: "8/10",
    review: "Review in 90 and 365 days"
  },
  {
    header: "Work-life decisions",
    decision: "taking a sabbatical from work",
    tags: ["#career", "#selfcare"],
    emotions: "liberated, uncertain",
    rating: "7/10",
    review: "Review in 30 and 180 days"
  },
  {
    header: "Family decisions",
    decision: "adopting a rescue dog",
    tags: ["#pets", "#responsibility"],
    emotions: "joyful, worried",
    rating: "9/10",
    review: "Review in 14 and 60 days"
  }
];

export function Hero({ onSignUp, onOpenDemo }: HeroProps) {
  const [currentCardIndex, setCurrentCardIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCardIndex((prev) => (prev + 1) % decisionCards.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const currentCard = decisionCards[currentCardIndex];

  return (
    <section className="relative py-20 md:py-32 overflow-hidden">
      {/* Animated Background Gradients */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgb(59,130,246,0.08)_0%,_rgb(147,197,253,0.12)_35%,_transparent_70%)] dark:bg-[radial-gradient(ellipse_at_center,_rgb(29,78,216,0.15)_0%,_rgb(59,130,246,0.08)_35%,_transparent_70%)] animate-gradient-shift"></div>
      
      {/* Secondary animated gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,_rgb(37,99,235,0.06)_0%,_transparent_50%)] dark:bg-[radial-gradient(circle_at_70%_60%,_rgb(29,78,216,0.1)_0%,_transparent_50%)] animate-pulse-glow"></div>
      
      {/* Bottom fade */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/30"></div>
      
      {/* Content */}
      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Content */}
          <div className="space-y-8">
            {/* Animated Icon */}
            <div className="flex justify-center lg:justify-start">
              <div className="animate-fade-in p-3 bg-blue-50/80 dark:bg-blue-950/50 backdrop-blur-sm rounded-full shadow-lg border border-blue-100/50 dark:border-blue-800/50">
                <BookOpen className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-float" />
              </div>
            </div>
            
            {/* Animated Headline */}
            <h1 className="animate-fade-in-delayed tracking-tight text-center lg:text-left">
              Make Better Decisions Through Thoughtful Reflection
            </h1>
            
            {/* Animated Description */}
            <p className="animate-fade-in-delayed-2 text-muted-foreground text-center lg:text-left max-w-xl">
              Decisionr helps you track your decisions, analyze outcomes, and learn from patterns. 
              Build better judgment through systematic reflection and data-driven insights.
            </p>
            
            {/* Animated Buttons */}
            <div className="animate-fade-in-delayed-3 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button 
                size="lg" 
                className="group shadow-lg bg-blue-600 hover:bg-blue-700 hover:scale-105 hover:shadow-xl dark:bg-blue-600 dark:hover:bg-blue-500 text-white transition-all duration-300" 
                onClick={onSignUp}
              >
                Start Your First Entry
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="group backdrop-blur-sm border-blue-200/50 dark:border-blue-700/50 bg-blue-50/20 dark:bg-blue-950/20 hover:bg-blue-50/40 dark:hover:bg-blue-950/40 text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 hover:scale-105 transition-all duration-300"
                onClick={onOpenDemo}
              >
                View Demo
              </Button>
            </div>
            
            {/* Animated Disclaimer */}
            <div className="animate-fade-in-delayed-4 text-sm text-muted-foreground text-center lg:text-left">
              Always own your data (full portability via json export)<br />
              AI-free application for privacy<br />
              No credit card required • 14-day free trial
            </div>
          </div>

          {/* Right Column - Decision Cards */}
          <div className="animate-scale-in-delayed relative flex items-center justify-center">
            {/* Main card container */}
            <div className="relative w-full max-w-md">
              {/* Gentle rotating border */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-blue-500/20 animate-rotate-gentle"></div>
              
              {/* Decision Card */}
              <div className="relative mx-4 my-8">
                <div key={currentCardIndex} className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-blue-200/30 dark:border-blue-700/30 animate-fade-in">
                  {/* Header */}
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">{currentCard.header}:</h3>
                  </div>
                  
                  {/* Decision text with typing animation */}
                  <div className="mb-4">
                    <div className="relative">
                      <span className="text-lg font-medium text-foreground">
                        <span className="inline-block overflow-hidden whitespace-nowrap animate-typewriter">{currentCard.decision}</span>
                      </span>
                    </div>
                  </div>
                  
                  {/* Tags with fade-in delay */}
                  <div className="mb-3 animate-fade-in-delayed-2">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Hash className="h-3 w-3" />
                      <span>tags: {currentCard.tags.join(", ")}</span>
                    </div>
                  </div>
                  
                  {/* Emotions with fade-in delay */}
                  <div className="mb-3 animate-fade-in-delayed-3">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Heart className="h-3 w-3" />
                      <span>emotions: {currentCard.emotions}</span>
                    </div>
                  </div>
                  
                  {/* Initial rating with fade-in delay */}
                  <div className="mb-3 animate-fade-in-delayed-4">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Star className="h-3 w-3" />
                      <span>Initial rating: {currentCard.rating}</span>
                    </div>
                  </div>
                  
                  {/* Review schedule with fade-in delay */}
                  <div className="animate-fade-in-delayed-4">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      <span>{currentCard.review}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating decorative icons */}
              <div className="absolute -top-4 -left-4 animate-float">
                <div className="p-2 bg-blue-100/80 dark:bg-blue-800/50 rounded-lg">
                  <Star className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div className="absolute -top-2 -right-6 animate-pulse-glow">
                <div className="p-2 bg-purple-100/80 dark:bg-purple-800/50 rounded-lg">
                  <Zap className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
              <div className="absolute -bottom-4 -right-2 animate-float" style={{animationDelay: '1s'}}>
                <div className="p-2 bg-green-100/80 dark:bg-green-800/50 rounded-lg">
                  <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}